import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/24/outline";
import { ToggleLeft, ToggleRight, Upload } from "lucide-react";
import UserTable from "../../components/users/UserTable";
import UserFilters from "../../components/users/UserFilters";
import BulkActionsBar from "../../components/users/BulkActionsBar";
import { usePageShortcuts } from "../../hooks/useKeyboardShortcuts";
import {
  UserFilterInput,
  OrgRole,
  AccountStatus,
  UserListItem,
} from "@shared/schemas";
import { trpc } from "../../lib/api/trpc";
import { useDeleteUser } from "../../features/users/hooks/useDeleteUser";
import { useOnboarding } from "../../features/users/hooks/useOnboarding";
import { useForcePasswordReset } from "../../features/users/hooks/useForcePasswordReset";

/**
 * Page for listing and managing users
 */
const UserListPage: React.FC = () => {
  const navigate = useNavigate();

  // State for filters and pagination
  const [filters, setFilters] = useState<UserFilterInput>({
    page: 0,
    limit: 10,
    orderBy: "createdAt",
    orderDirection: "desc",
    search: "",
  });

  // Get users data using the custom hook
  // @ts-ignore - Type checking for trpc.user property
  const { data, isLoading, isError, error } =
    trpc.user.getUsers.useQuery(filters);

  // Delete user mutation
  const { deleteUser, isLoading: isDeleting } = useDeleteUser();

  // Onboarding email hook
  const { sendOnboardingEmail, isSending: isSendingOnboarding } =
    useOnboarding();

  // Force password reset hook
  const { toggleForcePasswordReset } = useForcePasswordReset();

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserListItem | null>(null);

  // State for onboarding email confirmation dialog
  const [onboardingDialogOpen, setOnboardingDialogOpen] = useState(false);
  const [userToSendOnboarding, setUserToSendOnboarding] =
    useState<UserListItem | null>(null);

  // State for reset password confirmation dialog
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [userToResetPassword, setUserToResetPassword] =
    useState<UserListItem | null>(null);

  // State for bulk mode
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  // State for notifications
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });

  // Page-specific keyboard shortcuts
  usePageShortcuts([
    {
      key: "e",
      metaKey: true,
      action: () => setBulkMode((prev) => !prev),
      description: "Toggle bulk edit mode",
    },
    {
      key: "n",
      metaKey: true,
      action: () => navigate("/users/create"),
      description: "Create new user",
    },
    {
      key: "i",
      metaKey: true,
      action: () => navigate("/users/import"),
      description: "Import users",
    },
  ]);

  // Handle filter changes
  const handleSearchChange = (search: string) => {
    setFilters((prev) => ({ ...prev, search, page: 0 }));
  };

  const handleRoleChange = (role?: OrgRole) => {
    setFilters((prev) => ({ ...prev, role, page: 0 }));
  };

  const handleStatusChange = (status?: AccountStatus) => {
    setFilters((prev) => ({ ...prev, status, page: 0 }));
  };

  const handleResetFilters = () => {
    setFilters({
      page: 0,
      limit: 10,
      orderBy: "createdAt",
      orderDirection: "desc",
      search: "",
    });
  };

  // Handle pagination and sorting
  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleRowsPerPageChange = (limit: number) => {
    setFilters((prev) => ({ ...prev, limit, page: 0 }));
  };

  const handleSortChange = (
    orderBy: "firstName" | "lastName" | "email" | "role" | "createdAt",
    orderDirection: "asc" | "desc",
  ) => {
    setFilters((prev) => ({ ...prev, orderBy, orderDirection }));
  };

  // Handle user actions
  const handleAddUser = () => {
    navigate("/users/create");
  };

  const handleEditUser = (user: UserListItem) => {
    navigate(`/users/edit/${user.id}`);
  };

  const handleViewUser = (user: UserListItem) => {
    navigate(`/users/${user.id}`);
  };

  const handleResetPassword = (user: UserListItem) => {
    setUserToResetPassword(user);
    setResetPasswordDialogOpen(true);
  };

  const handleResetPasswordDialogClose = () => {
    setResetPasswordDialogOpen(false);
    setUserToResetPassword(null);
  };

  const handleResetPasswordConfirm = async () => {
    if (userToResetPassword) {
      const result = await toggleForcePasswordReset(
        userToResetPassword.id,
        true,
      );

      if (result.success) {
        setNotification({
          open: true,
          message: "Password reset flag set successfully",
          severity: "success",
        });
      } else {
        setNotification({
          open: true,
          message: `Failed to set password reset flag: ${result.error}`,
          severity: "error",
        });
      }
    }
    handleResetPasswordDialogClose();
  };

  // Delete user dialog handlers
  const handleDeleteDialogOpen = (user: UserListItem) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (userToDelete) {
      const result = await deleteUser(userToDelete.id);

      if (result.success) {
        setNotification({
          open: true,
          message: "User deleted successfully",
          severity: "success",
        });
      } else {
        setNotification({
          open: true,
          message: result.error || "Failed to delete user",
          severity: "error",
        });
      }
    }

    handleDeleteDialogClose();
  };

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  // Onboarding email dialog handlers
  const handleOnboardingDialogOpen = (user: UserListItem) => {
    setUserToSendOnboarding(user);
    setOnboardingDialogOpen(true);
  };

  const handleOnboardingDialogClose = () => {
    setOnboardingDialogOpen(false);
    setUserToSendOnboarding(null);
  };

  const handleOnboardingConfirm = async () => {
    if (userToSendOnboarding) {
      const result = await sendOnboardingEmail(userToSendOnboarding.id);

      if (result.success) {
        setNotification({
          open: true,
          message:
            userToSendOnboarding.accountStatus === "CREATED"
              ? "Onboarding email sent successfully"
              : "Onboarding email resent successfully",
          severity: "success",
        });
      } else {
        setNotification({
          open: true,
          message: result.error || "Failed to send onboarding email",
          severity: "error",
        });
      }
    }

    handleOnboardingDialogClose();
  };

  // Handle bulk mode toggle
  const handleBulkModeToggle = () => {
    setBulkMode((prev) => !prev);
    setSelectedUserIds([]);
  };

  // Handle bulk selection change
  const handleSelectionChange = (newSelectedIds: string[]) => {
    setSelectedUserIds(newSelectedIds);
  };

  // Handle bulk operation complete
  const handleBulkOperationComplete = () => {
    // Refresh the user list
    // @ts-ignore - Type checking for trpc.user property
    trpc.user.getUsers.invalidate();
  };

  // Get selected users for bulk actions bar
  const selectedUsers =
    data?.users.filter((user) => selectedUserIds.includes(user.id)) || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-serif text-3xl font-normal tracking-[-0.005em] text-[var(--text)]">
          Users
        </h1>
        <div className="flex gap-4 items-center">
          <button
            onClick={handleBulkModeToggle}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            {bulkMode ? (
              <>
                <ToggleRight className="mr-2 h-4 w-4" />
                Exit Bulk Mode
              </>
            ) : (
              <>
                <ToggleLeft className="mr-2 h-4 w-4" />
                Bulk Actions
              </>
            )}
          </button>
          <button
            onClick={() => navigate("/users/import")}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <Upload className="mr-2 h-4 w-4" />
            Import CSV
          </button>
          <button
            onClick={handleAddUser}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-accent-strong focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            Add User
          </button>
        </div>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700 mb-6" />

      {/* Filters */}
      <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6 mb-6">
        <UserFilters
          search={filters.search || ""}
          role={filters.role}
          status={filters.status}
          onSearchChange={handleSearchChange}
          onRoleChange={handleRoleChange}
          onStatusChange={handleStatusChange}
          onResetFilters={handleResetFilters}
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : isError ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <p className="text-red-800 dark:text-red-200">
            {error?.message || "An error occurred while loading users"}
          </p>
        </div>
      ) : (
        data && (
          <UserTable
            users={data.users}
            totalCount={data.pagination.totalCount}
            page={filters.page}
            rowsPerPage={filters.limit}
            orderBy={filters.orderBy}
            orderDirection={filters.orderDirection}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSortChange={handleSortChange}
            onEdit={handleEditUser}
            onDelete={handleDeleteDialogOpen}
            onResetPassword={handleResetPassword}
            onView={handleViewUser}
            onSendOnboardingEmail={handleOnboardingDialogOpen}
            bulkMode={bulkMode}
            selectedUserIds={selectedUserIds}
            onSelectionChange={handleSelectionChange}
          />
        )
      )}

      {/* Onboarding Email Confirmation Dialog */}
      {onboardingDialogOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleOnboardingDialogClose}
            />

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white dark:bg-darkmode-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-accent-tint sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      className="h-6 w-6 text-accent"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-500 dark:text-white">
                      {userToSendOnboarding?.accountStatus === "CREATED"
                        ? "Send Onboarding Email"
                        : "Resend Onboarding Email"}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {userToSendOnboarding?.accountStatus === "CREATED"
                          ? `Send an onboarding email to ${userToSendOnboarding?.firstName} ${userToSendOnboarding?.lastName}? They will receive instructions to set up their account.`
                          : `Resend the onboarding email to ${userToSendOnboarding?.firstName} ${userToSendOnboarding?.lastName}? They will receive a new link to complete their account setup.`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-darkmode-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleOnboardingConfirm}
                  disabled={isSendingOnboarding}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-accent text-base font-medium text-white hover:bg-accent-strong focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSendingOnboarding ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    "Send Email"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleOnboardingDialogClose}
                  disabled={isSendingOnboarding}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-darkmode-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-darkmode-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleDeleteDialogClose}
            />

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white dark:bg-darkmode-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      className="h-6 w-6 text-red-600 dark:text-red-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-500 dark:text-white">
                      Delete User
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete{" "}
                        {userToDelete
                          ? `${userToDelete.firstName} ${userToDelete.lastName}`
                          : "this user"}
                        ? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-darkmode-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    "Delete"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleDeleteDialogClose}
                  disabled={isDeleting}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-darkmode-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-darkmode-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Confirmation Dialog */}
      {resetPasswordDialogOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleResetPasswordDialogClose}
            />

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white dark:bg-darkmode-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 dark:bg-orange-900/20 sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      className="h-6 w-6 text-orange-600 dark:text-orange-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-500 dark:text-white">
                      Force Password Reset
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to force{" "}
                        {userToResetPassword?.firstName}{" "}
                        {userToResetPassword?.lastName} to reset their password
                        on next login?
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-darkmode-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleResetPasswordConfirm}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Force Reset
                </button>
                <button
                  type="button"
                  onClick={handleResetPasswordDialogClose}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-darkmode-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-darkmode-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification.open && (
        <div className="fixed bottom-4 right-4 z-50">
          <div
            className={`
            max-w-xl w-full shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden
            ${notification.severity === "success" ? "bg-green-50 dark:bg-green-900/20" : ""}
            ${notification.severity === "error" ? "bg-red-50 dark:bg-red-900/20" : ""}
            ${notification.severity === "warning" ? "bg-yellow-50 dark:bg-yellow-900/20" : ""}
            ${notification.severity === "info" ? "bg-accent-tint" : ""}
          `}
          >
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {notification.severity === "success" && (
                    <svg
                      className="h-6 w-6 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  )}
                  {notification.severity === "error" && (
                    <svg
                      className="h-6 w-6 text-red-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  )}
                </div>
                <div className="ml-3 flex-1 pt-0.5">
                  <p
                    className={`
                    text-sm font-medium
                    ${notification.severity === "success" ? "text-green-800 dark:text-green-200" : ""}
                    ${notification.severity === "error" ? "text-red-800 dark:text-red-200" : ""}
                    ${notification.severity === "warning" ? "text-yellow-800 dark:text-yellow-200" : ""}
                    ${notification.severity === "info" ? "text-accent-strong" : ""}
                  `}
                  >
                    {notification.message}
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0 flex">
                  <button
                    onClick={handleNotificationClose}
                    className={`
                      inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2
                      ${notification.severity === "success" ? "text-green-500 hover:text-green-600 focus:ring-green-500" : ""}
                      ${notification.severity === "error" ? "text-red-500 hover:text-red-600 focus:ring-red-500" : ""}
                      ${notification.severity === "warning" ? "text-yellow-500 hover:text-yellow-600 focus:ring-yellow-500" : ""}
                      ${notification.severity === "info" ? "text-accent hover:text-accent-strong focus:ring-accent" : ""}
                    `}
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {bulkMode && selectedUserIds.length > 0 && (
        <BulkActionsBar
          selectedUsers={selectedUsers}
          onClearSelection={() => setSelectedUserIds([])}
          onOperationComplete={handleBulkOperationComplete}
        />
      )}
    </div>
  );
};

export default UserListPage;
